import Decimal from 'decimal.js';

import {
  AccessParameterFrequency,
  AccessParameterKind,
  BeneficiaryLevel,
  ScheduledDistributionKind,
} from '@/types/schema';

import {
  AccessParameterRadioGroupKind,
  BeneficiaryFormScheduledDistribution,
  DistributionType,
  EntityBeneficiariesFormBeneficiary,
  EntityBeneficiariesSubformShape,
} from './EntityBeneficiariesSubform.types';
export const defaultBeneficiary = (): EntityBeneficiariesFormBeneficiary => {
  return {
    id: null,
    beneficiaryId: '',
    level: BeneficiaryLevel.Primary,
    notes: '',
    beneficiaryFormAccessParameters: {
      _accessParameterKind: AccessParameterRadioGroupKind.Full,
      kind: AccessParameterKind.Full,
      checkboxes: {
        [AccessParameterKind.FullDiscretion]: false,
        [AccessParameterKind.AllTrustIncome]: false,
        [AccessParameterKind.Hems]: false,
        [AccessParameterKind.Hms]: false,
        [AccessParameterKind.Other]: false,
      },
      values: {
        [AccessParameterKind.FullDiscretion]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: false,
          _hasAgeParams: false,
          ageParams: [
            {
              _ageRequirementKind: 'until',
            },
          ],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
        },
        [AccessParameterKind.AllTrustIncome]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: true,
          frequency: AccessParameterFrequency.Annually,
          _hasAgeParams: false,
          ageParams: [],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
          percentage: new Decimal(100) as unknown as undefined, // type erasure necessary to satisfy the OpaqueDecimal type
        },
        [AccessParameterKind.Hems]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: false,
          _hasAgeParams: false,
          ageParams: [
            {
              _ageRequirementKind: 'until',
            },
          ],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
        },
        [AccessParameterKind.Hms]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: false,
          _hasAgeParams: false,
          ageParams: [
            {
              _ageRequirementKind: 'until',
            },
          ],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
        },
        [AccessParameterKind.Other]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: false,
          _hasAgeParams: false,
          ageParams: [],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
        },
      },
      _hasAmountOrPercentage: false,
      amountsOrPercentages: [],
    },
    beneficiaryFormPowerOfAppointment: {
      _hasPowerOfAppointment: false,
      power: '',
    },
  };
};

export const defaultCharitableBeneficiary =
  (): EntityBeneficiariesFormBeneficiary => ({
    id: null,
    beneficiaryId: '',
    level: BeneficiaryLevel.Primary,
    notes: '',
    beneficiaryFormAccessParameters: {
      _accessParameterKind: AccessParameterRadioGroupKind.Partial,
      kind: AccessParameterKind.Full,
      checkboxes: {
        [AccessParameterKind.FullDiscretion]: false,
        [AccessParameterKind.AllTrustIncome]: false,
        [AccessParameterKind.Hems]: false,
        [AccessParameterKind.Hms]: false,
        [AccessParameterKind.Other]: false,
      },
      values: {
        [AccessParameterKind.FullDiscretion]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: false,
          _hasAgeParams: false,
          ageParams: [],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
        },
        [AccessParameterKind.AllTrustIncome]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: false,
          _hasAgeParams: false,
          ageParams: [],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
        },
        [AccessParameterKind.Hems]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: false,
          _hasAgeParams: false,
          ageParams: [],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
        },
        [AccessParameterKind.Hms]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: false,
          _hasAgeParams: false,
          ageParams: [],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
        },
        [AccessParameterKind.Other]: {
          kind: AccessParameterKind.Percentage,
          _hasFrequency: false,
          _hasAgeParams: false,
          ageParams: [],
          _hasAmountOrPercentage: false,
          distributionType: DistributionType.Discretionary,
        },
      },
      _hasAmountOrPercentage: false,
      amountsOrPercentages: [],
    },
    beneficiaryFormPowerOfAppointment: {
      _hasPowerOfAppointment: false,
      power: '',
    },
  });

export const entityBeneficiariesSubformDefaultValues: EntityBeneficiariesSubformShape =
  {
    beneficiaries: [],
    lifetimeBeneficiaries: [],
    remainderBeneficiaries: [],
    leadBeneficiary: defaultCharitableBeneficiary(),
    incomeBeneficiaries: [defaultCharitableBeneficiary()],
    charitableBeneficiaryType: 'annuity',
  };

export const defaultBeneficiaryFormScheduledDistribution: BeneficiaryFormScheduledDistribution =
  {
    _hasAgeParams: false,
    _ageRequirementKind: 'upon' as const,
    scheduledDistribution: {
      kind: ScheduledDistributionKind.Percentage,
      frequency: '',
    },
  };
